import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  Container,
  Box,
  TextField,
  IconButton,
  Button,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Paper,
  Chip,
  Stack,
  Tooltip,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";

const CreateOtherPostLink = () => {
  const [links, setLinks] = useState([""]);

  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [checkResult, setCheckResult] = useState({
    existingLinks: [],
    newLinks: [],
    invalidLinks: [],
  });

  const cleanedLinks = useMemo(() => {
    return links
      .map((l) => (typeof l === "string" ? l.trim() : ""))
      .filter(Boolean);
  }, [links]);

  const stats = useMemo(() => {
    return {
      total: cleanedLinks.length,
      existing: checkResult.existingLinks.length,
      fresh: checkResult.newLinks.length,
      invalid: checkResult.invalidLinks.length,
    };
  }, [cleanedLinks.length, checkResult.existingLinks.length, checkResult.newLinks.length, checkResult.invalidLinks.length]);

  const handleChange = (index, value) => {
    const newLinks = [...links];
    newLinks[index] = value;
    setLinks(newLinks);
  };

  const addInput = () => setLinks((p) => [...p, ""]);
  const removeInput = (index) => setLinks((p) => p.filter((_, i) => i !== index));

  const handlePreCheck = async () => {
    if (cleanedLinks.length === 0) {
      alert("Lütfen en az 1 link giriniz.");
      return;
    }

    try {
      setChecking(true);

      const res = await axios.post(
        "https://kariyer.biyomed.net/api/admin/checkOtherPostLinks",
        { links: cleanedLinks },
        { withCredentials: true }
      );

      const existingLinks = res.data.existingLinks || [];
      const newLinks = res.data.newLinks || [];
      const invalidLinks = res.data.invalidLinks || [];

      setCheckResult({ existingLinks, newLinks, invalidLinks });
      setConfirmOpen(true);
    } catch (err) {
      console.error("Check hata:", err);
      alert("Link kontrolü yapılırken hata oluştu.");
    } finally {
      setChecking(false);
    }
  };

  const handleSubmitConfirmed = async () => {
    const { newLinks } = checkResult;

    if (!newLinks || newLinks.length === 0) {
      alert("Yeni eklenecek link yok. Girdiğiniz linklerin hepsi zaten sistemde olabilir.");
      setConfirmOpen(false);
      return;
    }

    try {
      setSubmitting(true);

      const res = await axios.post(
        "https://kariyer.biyomed.net/api/admin/createOtherPostLink",
        { links: newLinks },
        { withCredentials: true }
      );

      alert(res.data.message ? res.data.message : "Linkler başarıyla sisteme eklendi!");
      setLinks([""]);
      setConfirmOpen(false);

      window.location.href = "/admin/create-other-post-link";
    } catch (err) {
      console.error("Submit hata:", err);
      alert("Link eklenirken hata oluştu.");
    } finally {
      setSubmitting(false);
    }
  };

  const isLikelyLinkedInJobLink = (url) =>
    typeof url === "string" && url.includes("linkedin.com/jobs/view/");

  return (
    <Container
      maxWidth="md"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        minHeight: "60vh",
        py: 4,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          bgcolor: "background.paper",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            background:
              "linear-gradient(135deg, rgba(25,118,210,0.10), rgba(156,39,176,0.08))",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                LinkedIn İlan Linki Ekle
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.75, color: "text.secondary" }}>
                Linkleri ekleyin, sistem önce kontrol etsin; sadece yeni olanlar için çekme işlemi başlatılsın.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip icon={<LinkIcon />} label={`Toplam: ${stats.total}`} />
              <Chip icon={<CheckCircleOutlineIcon />} color="success" variant="outlined" label={`Yeni: ${stats.fresh}`} />
              <Chip icon={<PlaylistAddCheckIcon />} color="info" variant="outlined" label={`Sistemde Var: ${stats.existing}`} />
              <Chip icon={<WarningAmberIcon />} color="warning" variant="outlined" label={`Geçersiz: ${stats.invalid}`} />
            </Stack>
          </Stack>
        </Box>

        {/* Info */}
        <Box sx={{ p: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Bu alan sadece <strong>LinkedIn iş ilanı linkleri</strong> içindir.
            Linkin içinde <code>linkedin.com/jobs/view/</code> geçmesine dikkat edin.
            Aynı ilan farklı formatlarda girilse bile sistem <strong>jobId</strong> üzerinden kontrol yapar.
          </Alert>

          {/* Inputs */}
          <Stack spacing={1.5}>
            {links.map((link, index) => {
              const showHint = link?.trim()?.length > 0;
              const ok = isLikelyLinkedInJobLink(link);

              return (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-start",
                  }}
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={`Link ${index + 1}`}
                    value={link}
                    onChange={(e) => handleChange(index, e.target.value)}
                    placeholder="https://www.linkedin.com/jobs/view/..."
                    helperText={
                      showHint
                        ? ok
                          ? "Format uygun görünüyor."
                          : "Link formatı şüpheli: linkedin.com/jobs/view/ içermiyor."
                        : " "
                    }
                    FormHelperTextProps={{
                      sx: { color: showHint ? (ok ? "success.main" : "warning.main") : "text.disabled" },
                    }}
                  />

                  <Stack direction="row" spacing={0.5} sx={{ pt: 0.5 }}>
                    {index > 0 && (
                      <Tooltip title="Bu alanı kaldır">
                        <IconButton
                          color="error"
                          onClick={() => removeInput(index)}
                          aria-label="Linki sil"
                        >
                          <CloseIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              onClick={addInput}
              startIcon={<AddCircleOutlineIcon />}
              sx={{ borderRadius: 2 }}
            >
              Yeni Link Alanı Ekle
            </Button>

            <Box sx={{ flex: 1 }} />

            <Button
              variant="contained"
              onClick={handlePreCheck}
              disabled={checking || submitting}
              sx={{ borderRadius: 2, minWidth: 220 }}
            >
              {checking ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} color="inherit" />
                  Kontrol ediliyor...
                </Box>
              ) : (
                "Linkleri Kontrol Et"
              )}
            </Button>
          </Stack>
        </Box>

        {/* Onay Dialog */}
        <Dialog
          open={confirmOpen}
          onClose={() => !submitting && setConfirmOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle sx={{ pb: 1 }}>
            Kontrol Sonucu
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              Devam ederseniz sadece <strong>yeni</strong> linkler için çekme işlemi başlatılır.
            </Typography>
          </DialogTitle>

          <DialogContent dividers sx={{ bgcolor: "background.default" }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
              <Chip icon={<LinkIcon />} label={`Toplam: ${stats.total}`} />
              <Chip icon={<CheckCircleOutlineIcon />} color="success" label={`Yeni: ${stats.fresh}`} />
              <Chip icon={<PlaylistAddCheckIcon />} color="info" label={`Sistemde Var: ${stats.existing}`} />
              <Chip icon={<WarningAmberIcon />} color="warning" label={`Geçersiz: ${stats.invalid}`} />
            </Stack>

            {checkResult.invalidLinks.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  <strong>{checkResult.invalidLinks.length}</strong> adet linkten jobId çıkarılamadı veya format uygun değil.
                  Bu linkler çekilmeyecek.
                </Alert>
                <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                  <List dense>
                    {checkResult.invalidLinks.map((l) => (
                      <ListItem key={l} sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={l}
                          primaryTypographyProps={{ variant: "body2" }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Box>
            )}

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              {/* Existing */}
              <Paper variant="outlined" sx={{ flex: 1, borderRadius: 2 }}>
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Sistemde Zaten Var ({checkResult.existingLinks.length})
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Bu linkler tekrar çekilmeyecek.
                  </Typography>
                </Box>
                <Divider />
                {checkResult.existingLinks.length === 0 ? (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Kayıtlı link bulunamadı.
                    </Typography>
                  </Box>
                ) : (
                  <List dense sx={{ maxHeight: 260, overflow: "auto" }}>
                    {checkResult.existingLinks.map((l) => (
                      <ListItem key={l} sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={l}
                          secondary="Kayıtlı olduğu için atlanacak"
                          primaryTypographyProps={{ variant: "body2" }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>

              {/* New */}
              <Paper
                variant="outlined"
                sx={{
                  flex: 1,
                  borderRadius: 2,
                  borderColor: checkResult.newLinks.length ? "success.light" : "divider",
                }}
              >
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Yeni Linkler ({checkResult.newLinks.length})
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Devam ederseniz çekme işlemi başlatılacak.
                  </Typography>
                </Box>
                <Divider />
                {checkResult.newLinks.length === 0 ? (
                  <Box sx={{ p: 2 }}>
                    <Alert severity="info">
                      Yeni link bulunamadı. (Girdiğiniz ilanların hepsi sistemde olabilir.)
                    </Alert>
                  </Box>
                ) : (
                  <List dense sx={{ maxHeight: 260, overflow: "auto" }}>
                    {checkResult.newLinks.map((l) => (
                      <ListItem key={l} sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={l}
                          primaryTypographyProps={{ variant: "body2" }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setConfirmOpen(false)} disabled={submitting} sx={{ borderRadius: 2 }}>
              Vazgeç
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitConfirmed}
              disabled={submitting || checkResult.newLinks.length === 0}
              sx={{ borderRadius: 2, minWidth: 240 }}
            >
              {submitting ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} color="inherit" />
                  İşlem başlatılıyor...
                </Box>
              ) : (
                "Devam Et ve Çekmeyi Başlat"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
};

export default CreateOtherPostLink;